import { Injectable } from '@angular/core';
import { ShepherdService } from 'angular-shepherd';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { StepOptionsAttachTo, StepOptionsButton } from 'shepherd.js';
import { getFromLocalStorage, setToLocalStorage } from './utilities';
import { cloneDeep } from 'lodash';

interface IButton extends StepOptionsButton {
  textTranslationKey?: string;
  type?: string;
}

interface IButtons {
  cancel: IButton,
  next: IButton,
  back: IButton,
  finish: IButton
}

interface ITourStep {
  attachTo?: StepOptionsAttachTo;
  titleTranslationKey: string;
  textTranslationKey: string[] | string;
  // image that will be displayed below text
  imgUrl?: string;
  // provide button definitions if default buttons don't meet requirements
  // e.g. custom on click button actions (make sure keepDefaults = false)
  buttons?: IButton[];

  // other options
  [key: string]: any;
}

interface StepWithMetadata {
  stepItself: ITourStep
  isFirstStep: boolean,
  isLastStep: boolean,
  isOnlyStep: boolean,
}

export class ITourOptions {
  // how the 'VIEWED' flag should be named in the local storage
  localStorageFlag: string = '';
  // set to true for minor changes - Will allow click events but will not render backdrop!
  noModal?: boolean = false;
  // set to true to fully override/customize
  // if set to false, provide button definitions in Tour Steps - 'this._buttons' will not be used
  keepDefaults?: boolean = true;
  // prepare the system to only start the tour until after an observable has been subscribed to.
  // i.e.: the reviews have to load first in order to bind to the `attachTo` hooks
  deferUntil?: Observable<any>;
  onTeardown?: Function;

  constructor(localStorageFlag: string = '', noModal: boolean = false, keepDefaults: boolean = true, deferUntil?: Observable<any>, onTeardown?: Function) {
    this.localStorageFlag = localStorageFlag;
    this.noModal = noModal;
    this.keepDefaults = keepDefaults;
    this.deferUntil = deferUntil;
    this.onTeardown = onTeardown;
  }
}

export enum ITourStepPosition {
  AUTO = 'auto',
  AUTO_START = 'auto-start',
  AUTO_END = 'auto-end',
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end' // use for STEP 1 positioned in the bottom-felt corner
}

const defaultOptions = {
  classes: 'tour-guide',
  useModalOverlay: true,
  scrollTo: false,
  cancelIcon: {
    enabled: false,
  },
  exitOnEsc: false,
  modal: true,
};

const tabClickCanceller = (e: KeyboardEvent) => {
  if (e.keyCode == 9) {
    e.preventDefault();
  }
};

@Injectable({
  providedIn: 'root',
})
export class TourGuideService {
  private _buttons: IButtons;

  constructor(
      protected shepherdService: ShepherdService,
      protected translate: TranslateService,
  ) {
    this.shepherdService.defaultStepOptions = defaultOptions;
    this.shepherdService.modal = true;
  }

  public init_default_buttons(): void {
    this._buttons = {
      cancel: {
        classes: 'cancel-button',
        text: this.translate.instant('TOUR_GUIDE.BUTTONS.CANCEL'),
        type: 'cancel',
      },
      next: {
        classes: 'next-button',
        text: this.translate.instant('TOUR_GUIDE.BUTTONS.NEXT'),
        type: 'next',
      },
      back: {
        classes: 'back-button',
        secondary: true,
        text: this.translate.instant('TOUR_GUIDE.BUTTONS.BACK'),
        type: 'back',
      },
      finish: {
        classes: 'finish-button',
        text: this.translate.instant('TOUR_GUIDE.BUTTONS.FINISH'),
        type: 'next',
      },
    };
  }

  /**
   * dynamically checks for the step position
   * & renders the indicator. Use with 'when'
   * https://docs.shepherdjs.dev/api/step/interfaces/stepoptions/#when
   */
  public progressOnShow() {
    const self = this;
    const step = self.shepherdService.tourObject.getCurrentStep();
    const all_steps = (self.shepherdService.tourObject.getCurrentStep() as any).tour.steps;

    const currentStepIndex = all_steps.indexOf(step);
    const footer = step.getElement().querySelector('.shepherd-footer');

    const progress = document.createElement('span');
    progress.className = 'progress-indicator';
    progress.innerText = `${currentStepIndex + 1}/${all_steps.length}`;

    footer.prepend(progress);
  }

  /**
   * reacts to step actions
   * more info at https://shepherdjs.dev/docs/Step.html
   */
  private whenTriggers(stepWithMetadata: StepWithMetadata) {
    const self = this;
    return {
      when: {
        ...stepWithMetadata.stepItself.when,
        show() {
          if (stepWithMetadata.isOnlyStep) {
            // no need to show the progress when dealing w/ 1-step tours
            return;
          }
          self.progressOnShow();
        },
      },
    };
  }

  /**
   * public getter
   */
  public get delegator(): ShepherdService {
    return this.shepherdService;
  }

  /**
   * Handle the cancellation logic.
   * @param setLocalstorageFlag
   * @param tourOptions
   * @param resetTour
   */
  private cancelTour(setLocalstorageFlag = true, tourOptions: ITourOptions, resetTour?: boolean) {
    // voluntary actions upon teardown
    if (tourOptions.onTeardown) {
      tourOptions.onTeardown();
    }

    if (setLocalstorageFlag) {
      setToLocalStorage(tourOptions.localStorageFlag, 'VIEWED');
    }

    // re-apply pointer events
    document.body.classList.remove('no-pointer-events');

    // re-apply tab click listener
    document.removeEventListener('keydown', tabClickCanceller);

    // lead to weird behavior when previously called on cancel event
    // and on language changed event as well in multi-step tours
    if (resetTour) {
      // the tour object lacks a single "flushSteps" method
      // so we've got to remove the steps one-by-one
      // https://shepherdjs.dev/docs/Tour.html#removeStep
      this.delegator.tourObject.steps?.map(step => {
        this.delegator.tourObject.removeStep(step.id);
      });
      this.delegator.cancel(); // the backdrop would sometime not disappear / not all steps
      // would be removed without calling this
    }

    // Now we've got a clean slate.
  }

  /**
   * Simply delegates to `ShepherdService` but is needed
   * b/c of a lib bug where the defaults aren't being correctly applied
   * @param steps
   * @param opts
   */
  public addStepsAndStartTour(
      steps: Array<ITourStep>,
      opts: ITourOptions = new ITourOptions(),
  ): ShepherdService {
    if (opts.noModal) { // disable modal mode
      this.shepherdService.modal = false;
    }

    if (!opts.deferUntil) {
      if (this.canStartTour(opts.localStorageFlag)) {
        this.prepareAndStartTour(steps, opts);
        return this.delegator;
      }
    } else {
      opts.deferUntil.subscribe((_) => {
        if (this.canStartTour(opts.localStorageFlag)) {
          this.prepareAndStartTour(steps, opts);
          return this.delegator;
        }
      });
    }
  }

  /**
   * Skip altogether when viewed or if a tour is already active (running 2+ tours can lead to issues)
   * @param localStorageFlag
   * @private
   */
  private canStartTour(localStorageFlag: string) {
    return !(getFromLocalStorage(localStorageFlag) === 'VIEWED' || this.delegator.isActive);
  }

  /**
   * Prepares steps and starts tour
   * @param steps
   * @param opts
   * @private
   */
  private prepareAndStartTour(steps: Array<ITourStep>,
                              opts: ITourOptions) {
    // generate default buttons
    this.init_default_buttons();

    // a deep copy to prevent original source modification
    let processedSteps = cloneDeep(steps);

    processedSteps.forEach(step => {
      // translate based on the current lang
      step.title = this.translate.instant(step.titleTranslationKey);
      step.text = this.translate.instant(step.textTranslationKey);
      step.buttons?.forEach((b) => b.text = this.translate.instant(b.textTranslationKey));

      // if there is an image to be displayed, make sure to render it below the text
      if (step.imgUrl) {
        const imgHtmlStr =
            `<img src=${step.imgUrl} alt="step image" ` +
            'style="width: 100%; border-radius: 8px; margin-top: 16px;">';
        step.text += imgHtmlStr;
      }
    });

    if (opts.keepDefaults) {
      processedSteps = processedSteps.map((step, index) =>
          ({
            // keep the original step params
            ...step,

            // extend them
            ...this.defaults({
              stepItself: step,
              // first would have a `cancel` button
              isFirstStep: index === 0,
              // last would have a `done` button
              isLastStep: index === processedSteps.length - 1,
              isOnlyStep: processedSteps.length === 1,
            }),
          }));
    }

    // standard API method
    this.delegator.addSteps(processedSteps);

    // b/c of an `angular-shepherd` bug
    // re-apply the defaults upon tour start
    // because they wouldn't be otherwise applied
    this.delegator.tourObject.on('start', () => {
      (this.delegator.tourObject as any).options = {
        // keep the current values
        ...(this.delegator.tourObject as any).options,
        // extend
        ...this.defaults,
      };
    });

    /*
     * ON START
     */

    // do not disable pointer events if opts.noModal
    this.delegator.tourObject.on('start', () => {
      if (!opts.noModal) {
        document.body.classList.toggle('no-pointer-events');
      }

      // needs to be cancelled globally b/c going from
      // field to field causes search rerenders which
      // messes up the experience
      document.addEventListener('keydown', tabClickCanceller);
    });

    /**
     * ON TEARDOWN
     */
    ['close', 'hide', 'complete', 'cancel'].forEach(
        (event) => this.delegator.tourObject
            .on(event, () => {
              // no need to cancel an inactive tour
              if (this.delegator.tourObject.isActive()) {
                this.cancelTour(true, opts);
              }
            }),
    );

    this.delegator.start();

    this.translate.onLangChange
        // 1 take only to prevent memory leaks. Any new `prepareAndStartTour` caller will
        // have a chance to register a new, 1-take subscription.
        .pipe(take(1))
        .subscribe(_ => {
          if (this.delegator.tourObject.isActive()) {
            // cancel w/o setting the localstorage flag to VIEWED
            this.cancelTour(false, opts, true);

            // run the tour again (with the up-to-date translations)
            this.prepareAndStartTour(steps, opts);
          }
        });
  };

  /**
   * Constructs repetitive shared defaults
   * @param opts
   */
  private defaults(opts: StepWithMetadata = {
    stepItself: {} as ITourStep,
    isFirstStep: false,
    isLastStep: false,
    isOnlyStep: false,
  }) {

    const buttons: Array<IButtons[keyof IButtons]> = [];

    // the 'back' and 'cancel' buttons are only applicable to
    // tours longer than 1 step so let's check whether we're dealing w/
    // 1-step tours
    if (!opts.isOnlyStep) {
      if (opts.isFirstStep) {
        buttons.push(this._buttons.cancel);
      } else {
        buttons.push(this._buttons.back);
      }
    }

    if (opts.isLastStep) {
      buttons.push(this._buttons.finish);
    } else {
      buttons.push(this._buttons.next);
    }

    return {
      // apply at the top level
      ...this.delegator.defaultStepOptions,
      // as well as the `defaultStepOptions` level
      defaultStepOptions: this.delegator.defaultStepOptions,

      ...this.whenTriggers(opts),

      buttons: buttons,
    };

  }

  /**
   * A reusable wrapper around the notifier making sure that the tour starts only when something has
   * been loaded already.
   * @param notifier
   * @private
   */
  public deferUntil(notifier: Observable<any>): Observable<boolean> {
    return notifier.pipe(
        // skip the call where e.g. an empty array is passed
        filter((val) => {
          // 1. Handle null/undefined first (to avoid errors in object check)
          if (val === null || val === undefined) return false;

          // 2. Check for empty Arrays
          if (Array.isArray(val)) {
            return val.length > 0;
          }

          // 3. Check for empty Objects (safe from null now)
          if (typeof val === 'object') {
            return Object.keys(val).length > 0;
          }

          // 4. Default truthiness (handles "", 0, false, etc.)
          return !!val;
        }),
        // subscribe only once
        take(1),
    );
  }
}
